import { useState, useEffect, useRef } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const cache = {};
let supportedCountries = null;

async function fetchSupportedCountries() {
  if (supportedCountries) return supportedCountries;
  try {
    const res = await fetch(`${API}/data/available-countries`);
    if (res.ok) {
      supportedCountries = new Set(await res.json());
    }
  } catch {
    // Network error — fall back to default supported countries below.
  }
  if (!supportedCountries) supportedCountries = new Set(["in", "us"]);
  return supportedCountries;
}

export function useGeoData(countryCode) {
  const cc = countryCode?.toLowerCase() || "in";
  const [cityData, setCityData] = useState(() => cache[cc]?.cities || []);
  const [industryData, setIndustryData] = useState(() => cache[cc]?.industries || []);
  const [loading, setLoading] = useState(!cache[cc]);
  const prevCc = useRef(cc);

  // Immediately sync state when country code changes
  if (prevCc.current !== cc) {
    prevCc.current = cc;
    if (cache[cc]) {
      setCityData(cache[cc].cities);
      setIndustryData(cache[cc].industries);
      setLoading(false);
    } else {
      setCityData([]);
      setIndustryData([]);
      setLoading(true);
    }
  }

  useEffect(() => {
    if (cache[cc]) return;
    let cancelled = false;
    const load = async () => {
      const supported = await fetchSupportedCountries();
      if (!supported.has(cc)) {
        cache[cc] = { cities: [], industries: [] };
        if (!cancelled) {
          setCityData([]);
          setIndustryData([]);
          setLoading(false);
        }
        return;
      }
      try {
        const [citiesRes, industriesRes] = await Promise.all([
          fetch(`${API}/data/${cc}/cities`),
          fetch(`${API}/data/${cc}/industries`),
        ]);
        const cities = citiesRes.ok ? await citiesRes.json() : [];
        const industries = industriesRes.ok ? await industriesRes.json() : [];
        cache[cc] = { cities, industries };
        if (!cancelled) {
          setCityData(cities);
          setIndustryData(industries);
          setLoading(false);
        }
      } catch {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [cc]);

  return { cityData, industryData, loading };
}
